// Página RegisterPayment - Formulario para registrar un pago a un proveedor
// Permite cargar comprobantes de pago (imágenes)

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getPurchaseOrder, registerPayment } from '../../services/purchaseOrders.service'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatCurrencyInput, parseCurrencyInput } from '../../utils/helpers'
import { Timestamp } from 'firebase/firestore'
import { uploadImage } from '../../services/cloudinary.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './RegisterPayment.css'

function RegisterPayment() {
  const { id, supplierId } = useParams()
  const navigate = useNavigate()
  const { user, userData } = useAuth()
  
  const [order, setOrder] = useState(null)
  const [supplier, setSupplier] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [editingFields, setEditingFields] = useState(new Set())
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  })
  
  const [receiptImages, setReceiptImages] = useState([]) // Array de {fileId, url, thumbnailUrl}
  const [uploadingImages, setUploadingImages] = useState(false)

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true)
      const orderData = await getPurchaseOrder(id)
      
      if (!orderData) {
        setNotification({
          type: 'error',
          message: 'Pedido de compra no encontrado'
        })
        setTimeout(() => navigate('/admin/pedidos-compra'), 2000)
        return
      }
      
      setOrder(orderData)
      
      // Buscar el proveedor
      const foundSupplier = orderData.suppliers?.find(s => s.supplierId === supplierId)
      if (!foundSupplier) {
        setNotification({
          type: 'error',
          message: 'Proveedor no encontrado en el pedido'
        })
        setTimeout(() => navigate(`/admin/pedidos-compra/${id}`), 2000)
        return
      }
      
      setSupplier(foundSupplier)
    } catch (error) {
      console.error('Error cargando pedido:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar el pedido: ' + (error.message || 'Error desconocido')
      })
    } finally {
      setLoading(false)
    }
  }, [id, supplierId, navigate])

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploadingImages(true)
    try {
      const uploadPromises = files.map(file => uploadImage(file, 'purchase-orders/receipts'))
      const uploadedImages = await Promise.all(uploadPromises)
      
      setReceiptImages(prev => [...prev, ...uploadedImages])
      
      setNotification({
        type: 'success',
        message: `${uploadedImages.length} imagen(es) cargada(s) correctamente`
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error subiendo imágenes:', error)
      setNotification({
        type: 'error',
        message: 'Error al subir las imágenes: ' + (error.message || 'Error desconocido')
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setUploadingImages(false)
      // Limpiar el input
      e.target.value = ''
    }
  }

  const handleRemoveImage = (index) => {
    setReceiptImages(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!paymentData.amount) {
      setNotification({
        type: 'error',
        message: 'El monto es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const amount = parseFloat(paymentData.amount)
    if (isNaN(amount) || amount <= 0) {
      setNotification({
        type: 'error',
        message: 'El monto debe ser mayor a 0'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const pending = (supplier?.subtotal || 0) - (supplier?.paidAmount || 0)
    if (amount > pending) {
      setNotification({
        type: 'error',
        message: `El monto no puede exceder el pendiente: ${formatCurrency(pending)}`
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)
    try {
      const paymentDate = paymentData.paymentDate 
        ? Timestamp.fromDate(new Date(paymentData.paymentDate))
        : Timestamp.now()

      await registerPayment(id, supplierId, {
        amount,
        paymentMethod: paymentData.paymentMethod,
        paymentDate,
        notes: paymentData.notes || null,
        receiptImages: receiptImages.length > 0 ? receiptImages : null,
        createdBy: user?.uid,
        createdByName: userData?.name || user?.email
      })

      setNotification({
        type: 'success',
        message: 'Pago registrado correctamente'
      })
      
      setTimeout(() => {
        navigate(`/admin/pedidos-compra/${id}`)
      }, 1500)
    } catch (error) {
      console.error('Error registrando pago:', error)
      setNotification({
        type: 'error',
        message: 'Error al registrar el pago: ' + (error.message || 'Error desconocido')
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="register-payment-page">
        <div className="loading-state">
          <p>Cargando datos del pedido...</p>
        </div>
      </div>
    )
  }

  if (!order || !supplier) {
    return (
      <div className="register-payment-page">
        <div className="error-state">
          <p>No se encontró el pedido o el proveedor</p>
          <Link to="/admin/pedidos-compra">
            <Button>Volver a Pedidos de Compra</Button>
          </Link>
        </div>
      </div>
    )
  }

  const pending = (supplier.subtotal || 0) - (supplier.paidAmount || 0)

  return (
    <div className="register-payment-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="page-header">
        <div>
          <Link to={`/admin/pedidos-compra/${id}`} className="back-link">
            ← Volver al Pedido
          </Link>
          <h1>Registrar Pago - {supplier.supplierName}</h1>
          <p className="page-subtitle">Pedido: {order.orderNumber}</p>
        </div>
      </div>

      <div className="payment-info-card">
        <h3>Información del Proveedor</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Subtotal:</span>
            <span className="info-value">{formatCurrency(supplier.subtotal || 0)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Pagado:</span>
            <span className="info-value">{formatCurrency(supplier.paidAmount || 0)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Pendiente:</span>
            <span className="info-value pending-amount">{formatCurrency(pending)}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-section">
          <h3>Datos del Pago</h3>
          
          <div className="form-group">
            <label htmlFor="amount">
              Monto a Pagar <span className="required">*</span>
            </label>
            <input
              type="text"
              id="amount"
              value={
                editingFields.has('amount')
                  ? (paymentData.amount ? `$${paymentData.amount.replace('.', ',')}` : '')
                  : (paymentData.amount ? formatCurrencyInput(paymentData.amount, true) : '')
              }
              onChange={(e) => {
                let cleanedValue = e.target.value.replace(/[^0-9.,]/g, '')
                if (!cleanedValue) {
                  setPaymentData({ ...paymentData, amount: '' })
                  return
                }
                cleanedValue = cleanedValue.replace('.', ',')
                const parts = cleanedValue.split(',')
                if (parts.length > 2) {
                  cleanedValue = parts[0] + ',' + parts.slice(1).join('')
                }
                setPaymentData({ ...paymentData, amount: cleanedValue })
              }}
              onFocus={() => setEditingFields(prev => new Set(prev).add('amount'))}
              onBlur={() => {
                const numericValue = parseCurrencyInput(paymentData.amount || '0')
                setPaymentData({ ...paymentData, amount: numericValue > 0 ? numericValue.toString() : '' })
                setEditingFields(prev => {
                  const newSet = new Set(prev)
                  newSet.delete('amount')
                  return newSet
                })
              }}
              placeholder="$0"
              required
              disabled={saving}
            />
            <small className="form-hint">
              Pendiente: {formatCurrency(pending)}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="paymentMethod">
              Método de Pago <span className="required">*</span>
            </label>
            <select
              id="paymentMethod"
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
              required
              disabled={saving}
            >
              <option value="cash">Efectivo</option>
              <option value="transfer">Transferencia</option>
              <option value="check">Cheque</option>
              <option value="credit_card">Tarjeta de Crédito</option>
              <option value="debit_card">Tarjeta de Débito</option>
              <option value="other">Otro</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="paymentDate">Fecha de Pago</label>
            <input
              type="date"
              id="paymentDate"
              value={paymentData.paymentDate}
              onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
              disabled={saving}
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notas</label>
            <textarea
              id="notes"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              rows="4"
              placeholder="Notas adicionales sobre el pago..."
              disabled={saving}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Comprobantes de Pago</h3>
          <p className="section-description">
            Sube imágenes de los comprobantes de pago (recibos, transferencias, cheques, etc.)
          </p>

          <div className="form-group">
            <label htmlFor="receiptImages" className="file-upload-label">
              <span className="upload-icon">📎</span>
              Seleccionar Imágenes
            </label>
            <input
              type="file"
              id="receiptImages"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              disabled={saving || uploadingImages}
              className="file-input"
            />
            {uploadingImages && (
              <p className="upload-status">Subiendo imágenes...</p>
            )}
          </div>

          {receiptImages.length > 0 && (
            <div className="images-preview">
              <h4>Imágenes Cargadas ({receiptImages.length})</h4>
              <div className="images-grid">
                {receiptImages.map((image, index) => (
                  <div key={index} className="image-preview-item">
                    <img 
                      src={image.thumbnailUrl || image.url} 
                      alt={`Comprobante ${index + 1}`}
                      className="preview-image"
                    />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                      disabled={saving}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <Link to={`/admin/pedidos-compra/${id}`}>
            <Button type="button" variant="secondary" disabled={saving}>
              Cancelar
            </Button>
          </Link>
          <Button type="submit" variant="primary" disabled={saving || uploadingImages}>
            {saving ? 'Registrando...' : 'Registrar Pago'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default RegisterPayment

